import React from "react";

import { Grid } from "@material-ui/core";

import {
  Edit,
  maxLength,
  minLength,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
  useRedirect,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { AclType, REGEX_PHONE } from "../../../utils/constants";
import { isCarrierAdmin, parsePhoneDigits } from "../../../utils/carrierUserUtils";
import { getBackPath } from "../../../utils/utils";
import { ValidateSaveButton } from "./ValidateSaveButton";
import { DeleteAccountButton, DeleteUserButton } from "./DeepDeleteButton";
import { SuspendDriverButton } from "./SuspendDriverButton";
import { CarrierAside } from "./CarrierUserAside";
import { CarrierUserYardAccess } from "./CarrierUserYardAccess";

const validateRequired = required();
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

const CarrierEditForm = ({ record, appUser, isSelf, ...props }) => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <TextInput
          source="firstName"
          label="First Name"
          variant="outlined"
          fullWidth
          validate={validateRequired}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          source="lastName"
          label="Last Name"
          variant="outlined"
          fullWidth
          validate={validateRequired}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          source="email"
          type="email"
          variant="outlined"
          fullWidth
          parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          source="phone"
          type="tel"
          variant="outlined"
          fullWidth
          parse={parsePhoneDigits}
          validate={validatePhone}
        />
      </Grid>
      {/* Reported, not asked. Access follows the company: a colleague works
          every transload it does, and a transload that wants one of its own
          people out blocks or removes them there. */}
      <CarrierUserYardAccess record={record} appUser={appUser} isSelf={isSelf} />
    </Grid>
  );
};

const CarrierEditActions = ({ data, canManageUsers, ...props }) => (
  <TopToolbar>
    {canManageUsers && <SuspendDriverButton record={data} />}
    <BackButton {...props} />
  </TopToolbar>
);

const CarrierEditToolbar = ({
  record,
  appUser,
  isSelf,
  backPath,
  ...props
}) => {
  return (
    <Toolbar style={{ justifyContent: "space-between" }} {...props}>
      <ValidateSaveButton
        {...props}
        record={record}
        appUser={appUser}
        isSelf={isSelf}
        backPath={backPath}
      />
      {/* Leaving the company is the whole account, and each profile offers only
          its own. A single transload is a transload manager's to withdraw, not
          this form's. Siblings rather than a fragment: the toolbar clones each
          child. */}
      {isSelf && <DeleteAccountButton record={record} backPath={backPath} />}
      {!isSelf && <DeleteUserButton record={record} appUser={appUser} />}
    </Toolbar>
  );
};

export const CarrierEdit = (props) => {
  const { appUser, isAuthorized } = useUser();
  const { basePath, record } = useEditController(props);
  const redirect = useRedirect();
  const canManageUsers = isAuthorized(AclType.USERS);
  const backPath = canManageUsers ? getBackPath(props) : ["/"];

  // A user managing colleagues edits them here; anyone else sees another user's
  // profile read-only, as before. The primary (admin) account is nobody's to
  // edit but their own: deleting them is exactly the takeover this role exists
  // to prevent, so colleagues get the read-only view.
  const isSelf = !record || !appUser || record.id === appUser.id;
  const targetIsAdmin = isCarrierAdmin(record);
  if (!isSelf && (!canManageUsers || targetIsAdmin))
    redirect("show", basePath, record.id);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit User" />}
      mutationMode="pessimistic"
      actions={
        <CarrierEditActions backPath={backPath} canManageUsers={canManageUsers} />
      }
      aside={<CarrierAside {...props} />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <CarrierEditToolbar
            record={record}
            appUser={appUser}
            isSelf={isSelf}
            backPath={backPath}
          />
        }
      >
        <CarrierEditForm record={record} appUser={appUser} isSelf={isSelf} />
      </SimpleForm>
    </Edit>
  );
};
